import { Button, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import "./KeyContactInformation.scss";
import { getLocalizedValueSync } from "../../services/localizationService";
import { useAppContext } from "../../provider/AppContextProvider";
import { useSilentTokenAcquire } from "../../hooks/useSilentTokenAcquire";
import { logger } from "../../services/logger";
import { fetchContactInfo, ContactInfo } from "../../services/contactInfoService";

const KeyContactInformation = () => {
  const { updateLoading, isInitializing } = useAppContext();
  const silentTokenAcquireHook = useSilentTokenAcquire();
  const calledOnceRef = useRef(false);

  const [contacts, setContacts] = useState<ContactInfo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  // Effect to handle loading states
  useEffect(() => {
    updateLoading(loading, "loadingKeyContactInformation");
  }, [loading, updateLoading]);

  useEffect(() => {
    const authToken = sessionStorage.getItem("authToken") || "";
    if (isInitializing || !authToken) return;

    const fetchContacts = async () => {
      setLoading(true);
      updateLoading(true, "loadingKeyContactInformation");
      try {
        const result = await fetchContactInfo({ authToken, silentTokenAcquireHook });
        setContacts(result || []);
      } catch (error: any) {
        logger.logToApi({
          type: "Error",
          source: "KeyContactInformation.tsx",
          eventCode: "FETCH_CONTACTINFO_ERROR",
          error: `${error.message}\n${error.stack || ""}`,
        }, silentTokenAcquireHook);
        logger.logToAppInsights(error as Error, 3);
        setContacts([]);
      } finally {
        setLoading(false);
        updateLoading(false, "loadingKeyContactInformation");
      }
    };

    if (!calledOnceRef.current && !isInitializing) {
      calledOnceRef.current = true;
      fetchContacts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isInitializing]);

  const isValidEmail = (email: string) =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const getEmailAddresses = (list: ContactInfo[]) =>
    list
      .map(c => c.email?.trim())
      .filter(e => !!e && isValidEmail(e))
      .join(";");

  const localizedHeader = getLocalizedValueSync("CA.keyContactInformation.header") || "";
  const localizedPhone = getLocalizedValueSync("CA.Phone") || "";
  const localizedEmail = getLocalizedValueSync("CA.Email") || "";
  const localizedEmailNow = getLocalizedValueSync("CA.keyContactInformation.EmailNow") || "";
  const visibleContacts = contacts.slice(0, 2);
  return (
    <>
      {visibleContacts.length > 0 && (
        <div className="key-contact-information-container">
          <Typography variant="h4" className="key-contact-headline">
            {localizedHeader}
          </Typography>
          <div className="key-contact-information">
            {visibleContacts.map((contact, index) => (
              <div key={index} className="key-contact-contact-info">
                <p className="key-contact-detail-info">
                  <b>{contact.name}</b>
                </p>
                <p className="key-contact-detail-info">{contact.title}</p>
                {contact.phoneNumber && (
                  <p className="key-contact-detail-info">
                    <b>{localizedPhone}:</b> {contact.phoneNumber}
                  </p>
                )}
                {contact.email && contact.email.trim() !== "" && (
                  <p className="key-contact-detail-info">
                    <b>{localizedEmail}:</b>{" "}
                    {isValidEmail(contact.email) ? (
                      <a href={`mailto:${contact.email}`}>{contact.email}</a>
                    ) : (
                      contact.email
                    )}
                  </p>
                )}
              </div>
            ))}
            <div className="key-contact-button-area">
              <button className="key-contact-button">
                <a
                  href={`mailto:${getEmailAddresses(visibleContacts)}`}
                  className="key-contact-email-button"
                >
                  {localizedEmailNow}
                </a>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default KeyContactInformation;